import { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import type { AppStep, AnalysisOption, EvinAnalysisResult, SocialPostMeta, AnalysisType, CreativeIdea, SuggestedFix, EventActivationMeta } from '../shared/types';
import { ANALYSIS_OPTIONS } from '../shared/types';
import { useTheme } from './hooks/useTheme';
import { tintVars } from './utils/tints';
import { useAnalysisJobs, type SidebarItem, type AppNotification } from './hooks/useAnalysisJobs';
import { usePromoRedemption, type PromoNotice } from './hooks/usePromoRedemption';
import { usePurchaseReturn } from './hooks/usePurchaseReturn';
import { primeAudio } from './utils/sound';
import { useAuth } from './contexts/AuthContext';
import AuthGate from './components/AuthGate';
import TermsGate from './components/TermsGate';
import ProfileGate from './components/ProfileGate';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import AccountPopover from './components/AccountPopover';
import AnalysisTypeSelector from './components/AnalysisTypeSelector';
import ContentUploader from './components/ContentUploader';
import CreativeBriefForm from './components/CreativeBriefForm';
import SocialPostHub from './components/SocialPostHub';
import HistoriquePage from './components/HistoriquePage';
import Loader from './components/Loader';
import ResultsDisplay from './components/ResultsDisplay';
import CreativeResults from './components/CreativeResults';
import PricingPage from './components/PricingPage';
import OnboardingScreen from './components/OnboardingScreen';
import AccountSettingsPage from './components/AccountSettingsPage';
import DisclaimerModal from './components/DisclaimerModal';
import LegalNotice from './components/LegalNotice';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Subprocessors from './components/Subprocessors';
import Footer from './components/Footer';
import AnalysisToast from './components/AnalysisToast';

const ShaderBackground = lazy(() => import('./components/ui/ShaderBackground'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));

/**
 * Résultat de l'échange d'un code promo, annoncé au-dessus du choix du type
 * d'analyse. Volontairement posé LÀ et pas en modale : le visiteur qui vient
 * de scanner une carte doit voir ses analyses créditées ET les neuf types
 * disponibles dans le même coup d'œil, puis choisir librement — on ne le
 * pousse pas vers un parcours imposé.
 * Sert aussi au message de retour de paiement Stripe (usePurchaseReturn).
 */
function PromoNoticeBanner({ notice, onDismiss }: { notice: PromoNotice; onDismiss: () => void }) {
  const tone =
    notice.kind === 'success'
      ? 'bg-emerald-50 dark:bg-emerald-900/20 shader:bg-emerald-500/10 border-emerald-200 dark:border-emerald-800 shader:border-emerald-400/25'
      : notice.kind === 'info'
        ? 'bg-slate-50 dark:bg-slate-800/50 shader:bg-white/5 border-slate-200 dark:border-slate-700 shader:border-white/10'
        : 'bg-orange-50 dark:bg-orange-900/20 shader:bg-orange-500/10 border-orange-200 dark:border-orange-800 shader:border-orange-400/25';

  return (
    <div className={`mb-6 flex items-start gap-3 px-4 py-3 rounded-xl border ${tone}`}>
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-900 dark:text-white shader:text-white">
          {notice.title}
        </p>
        {notice.message && (
          <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400 shader:text-white/75">
            {notice.message}
          </p>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Fermer"
        className="p-1 -m-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shader:text-white/50 shader:hover:text-white transition-colors"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/** Charge utile soumise par les formulaires d'analyse. */
type SubmitData = {
  textContent?: string;
  context?: string;
  images?: File[];
  social?: SocialPostMeta;
  event?: EventActivationMeta;
};

/** Construit le résumé d'entrée (titre de la discussion) — même logique que
 *  l'ancien addToHistory, dont le cas spécial creative_brief. */
function buildInputSummary(option: AnalysisOption, data: SubmitData): string {
  let inputSummary =
    data.textContent ||
    (data.images && data.images.length > 0 ? data.images.map((f) => f.name).join(', ') : option.label);
  if (option.key === 'creative_brief' && data.textContent) {
    try {
      const brief = JSON.parse(data.textContent);
      inputSummary = [brief.brand, brief.product].filter(Boolean).join(' — ') || option.label;
    } catch {
      /* keep raw */
    }
  }
  return inputSummary;
}

export default function App() {
  const { theme, toggle: toggleTheme } = useTheme();
  const { features, organization, isOnboarding, refreshProfile } = useAuth();
  const jobs = useAnalysisJobs();
  // Encaisse le code promo d'une carte de visite salon dès l'ouverture de
  // session, puis annonce le résultat en haut de l'écran de sélection.
  const promo = usePromoRedemption();
  // Retour de Stripe Checkout : confirmation du pack ou de l'abonnement, une
  // fois les crédits ou le plan réellement visibles (cf. usePurchaseReturn).
  const purchase = usePurchaseReturn();

  // Compteur de crédits de l'accueil : une analyse réserve son crédit au
  // démarrage et le rembourse si elle échoue. On recharge donc le profil
  // (crédits par type compris) quand le nombre d'analyses en cours change.
  const activeJobs = jobs.runningCount + jobs.queuedCount;
  const prevActiveJobs = useRef(activeJobs);
  useEffect(() => {
    if (prevActiveJobs.current === activeJobs) return;
    prevActiveJobs.current = activeJobs;
    void refreshProfile();
  }, [activeJobs, refreshProfile]);

  const [step, setStep] = useState<AppStep>('select');
  const [selectedOption, setSelectedOption] = useState<AnalysisOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Affichage d'une analyse ouverte depuis l'Historique serveur (lecture seule,
  // non ancrée à un item de la sidebar — comme l'ancien comportement).
  const [externalResult, setExternalResult] = useState<{ result: EvinAnalysisResult; option: AnalysisOption | null } | null>(null);
  const [legalVisible, setLegalVisible] = useState(false);
  const [privacyVisible, setPrivacyVisible] = useState(false);
  const [termsVisible, setTermsVisible] = useState(false);
  const [subprocessorsVisible, setSubprocessorsVisible] = useState(false);

  // Sidebar / chrome state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [accountInitialTab, setAccountInitialTab] = useState<'menu' | 'scoring' | 'transparency'>('menu');
  const [pricingScrollTo, setPricingScrollTo] = useState<string | undefined>();

  const activeItem = jobs.items.find((i) => i.id === jobs.activeId) ?? null;
  const activeOption = activeItem ? ANALYSIS_OPTIONS.find((o) => o.key === activeItem.analysisType) ?? null : null;

  // Si l'utilisateur regarde déjà une analyse qui vient de se terminer, on
  // marque sa notification comme lue (pas de pastille fantôme pour ce qu'il voit).
  const viewingAnalysis = step === 'result' || step === 'loading';
  const activeItemDoneId = activeItem && activeItem.status === 'done' && viewingAnalysis ? activeItem.id : null;
  useEffect(() => {
    if (activeItemDoneId) jobs.markReadByItem(activeItemDoneId);
    // jobs.markReadByItem volontairement hors deps (identité instable, no-op si rien à lire)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItemDoneId]);

  const handleSelectType = useCallback(
    (option: AnalysisOption) => {
      setSelectedOption(option);
      setError(null);
      setExternalResult(null);
      jobs.setActiveId(null);
      setStep('upload');
    },
    [jobs],
  );

  // Met une analyse en file d'attente. La discussion apparaît instantanément
  // dans la sidebar (item 'queued'/'running'), l'écran bascule sur le loader,
  // mais le résultat ne s'affiche PAS de force : à la fin, une notification est
  // émise. Si l'utilisateur reste sur cet écran, il voit le résultat arriver.
  const handleSubmit = useCallback(
    (data: SubmitData) => {
      if (!selectedOption) return;
      primeAudio(); // débloque l'audio dans le geste utilisateur (son de fin d'analyse)
      const inputSummary = buildInputSummary(selectedOption, data);
      const id = jobs.enqueue({
        analysisType: selectedOption.key,
        label: selectedOption.label,
        inputSummary,
        payload: data,
      });
      setExternalResult(null);
      jobs.setActiveId(id);
      setStep('result');
      setError(null);
    },
    [selectedOption, jobs],
  );

  // Deep analyze d'une idée créative (chemin Évin Créa — actuellement masqué).
  const handleDeepAnalyze = useCallback(
    (idea: CreativeIdea) => {
      const conceptOption = ANALYSIS_OPTIONS.find((o) => o.key === 'concept')!;
      const textContent = [
        `TERRITOIRE CRÉATIF : ${idea.territory}`,
        `\nCONCEPT : ${idea.concept}`,
        `\nSLOGANS PROPOSÉS :\n${idea.slogans.map((s, i) => `${i + 1}. « ${s} »`).join('\n')}`,
        `\nDIRECTION ARTISTIQUE : ${idea.visualDirection}`,
      ].join('\n');
      const id = jobs.enqueue({
        analysisType: 'concept',
        label: conceptOption.label,
        inputSummary: `Évin Créa → ${idea.territory}`,
        payload: {
          textContent,
          context: `Idée générée par Évin Créa. TERRITOIRE CRÉATIF CHOISI : « ${idea.territory} ». IMPORTANT : toute suggestion de version conforme DOIT rester dans ce territoire créatif. Ne pas dériver vers un autre axe (terroir, savoir-faire, etc.) sauf si le territoire choisi est fondamentalement incompatible avec la Loi Évin.`,
        },
      });
      setExternalResult(null);
      jobs.setActiveId(id);
      setStep('result');
    },
    [jobs],
  );

  const handleBack = useCallback(() => {
    if (step === 'upload') {
      setSelectedOption(null);
      setStep('select');
    } else if (step === 'result') {
      setStep('upload');
    }
  }, [step]);

  const handleReset = useCallback(() => {
    setSelectedOption(null);
    setError(null);
    setExternalResult(null);
    jobs.setActiveId(null);
    setStep('select');
    // Rafraîchit les crédits par type : un crédit consommé se reflète aussitôt.
    void refreshProfile();
  }, [refreshProfile, jobs]);

  const handleViewHistory = useCallback(
    (item: SidebarItem) => {
      // Backfill originalInput pour les anciens items sans cette donnée.
      if (item.result && !item.result.originalInput && item.title
        && !/\.(jpe?g|png|gif|webp|bmp|svg|heic)$/i.test(item.title)) {
        item.result.originalInput = item.title;
      }
      setExternalResult(null);
      jobs.setActiveId(item.id);
      jobs.markReadByItem(item.id);
      setSelectedOption(ANALYSIS_OPTIONS.find((o) => o.key === item.analysisType) ?? null);
      setStep('result');
      setIsMobileMenuOpen(false);
    },
    [jobs],
  );

  const handleDeleteHistory = useCallback(
    (id: string) => {
      const wasActive = jobs.activeId === id;
      jobs.deleteItem(id);
      if (wasActive) handleReset();
    },
    [jobs, handleReset],
  );

  // Ancre une réécriture conforme générée à son analyse (live + cache).
  const handleSuggestionSaved = useCallback(
    (fix: SuggestedFix) => {
      if (externalResult) {
        setExternalResult((prev) => (prev ? { ...prev, result: { ...prev.result, suggestedFix: fix } } : prev));
        return;
      }
      if (jobs.activeId && activeItem?.result) {
        jobs.updateResult(jobs.activeId, { ...activeItem.result, suggestedFix: fix });
      }
    },
    [externalResult, jobs, activeItem],
  );

  // Ouvre une analyse depuis une notification.
  const handleOpenNotification = useCallback(
    (notif: AppNotification) => {
      const item = jobs.items.find((i) => i.id === notif.itemId);
      jobs.markRead(notif.id);
      if (!item) return;
      setExternalResult(null);
      jobs.setActiveId(item.id);
      setSelectedOption(ANALYSIS_OPTIONS.find((o) => o.key === item.analysisType) ?? null);
      setStep('result');
    },
    [jobs],
  );

  // Pill « analyse en cours » → revient sur l'analyse active la plus récente.
  const handleResumeLoader = useCallback(() => {
    const running = jobs.items.find((i) => i.status === 'running') ?? jobs.items.find((i) => i.status === 'queued');
    if (!running) return;
    setExternalResult(null);
    jobs.setActiveId(running.id);
    setStep('result');
  }, [jobs]);

  /* Repère coloré de navigation (mode pastel) : dès qu'on entre dans une
     analyse, la teinte de la tuile d'où l'on vient habille l'écran jusqu'au
     retour — tout l'intérieur du cadre prend sa teinte (`p-frame-tinted`),
     en-tête et cartes suivent via les variables `--tint-*` posées sur le
     cadre (utils/tints.ts). Hors mode pastel, personne ne lit ces
     variables : aucun effet sur les thèmes clair / sombre / shader. */
  const tintedStep = step === 'upload' || step === 'result' || step === 'loading';
  const tintedType = tintedStep
    ? (step === 'upload' ? selectedOption?.key : externalResult?.option?.key ?? activeOption?.key ?? selectedOption?.key)
    : null;
  const inAnalysis = tintedStep && !!tintedType;

  function renderAnalysisView() {
    if (externalResult) {
      const r = externalResult.result;
      if (r.creativeResult) {
        return <CreativeResults creativeResult={r.creativeResult} sources={r.sources} onNewAnalysis={handleReset} onDeepAnalyze={handleDeepAnalyze} />;
      }
      return (
        <ResultsDisplay
          key="external"
          result={r}
          onNewAnalysis={handleReset}
          onSuggestionSaved={handleSuggestionSaved}
          analysisLabel={externalResult.option?.label}
          analysisType={externalResult.option?.key}
        />
      );
    }
    if (!activeItem) return null;
    if (activeItem.status === 'queued' || activeItem.status === 'running') {
      return (
        <Loader
          analysisType={activeItem.analysisLabel}
          progress={activeItem.status === 'queued' ? null : activeItem.progress ?? null}
          progressLabel={activeItem.status === 'queued' ? "En file d'attente…" : activeItem.progressLabel ?? null}
        />
      );
    }
    if (activeItem.status === 'error') {
      return (
        <div className="animate-fade-in max-w-md mx-auto text-center py-16">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mb-4">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
          </div>
          <h3 className="font-display text-lg text-slate-900 dark:text-white mb-1">L'analyse a échoué</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">{activeItem.errorMessage ?? 'Une erreur est survenue.'}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mb-6">Aucun crédit n'a été débité.</p>
          <div className="flex items-center justify-center gap-2">
            <button onClick={handleReset} className="font-display px-4 py-2 rounded-lg pastel:rounded-full text-sm border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Retour</button>
            <button onClick={() => jobs.retryItem(activeItem.id)} className="font-display px-4 py-2 rounded-lg pastel:rounded-full text-sm text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 pastel:bg-none pastel:bg-violet-500 pastel:hover:bg-violet-600 transition-colors">Réessayer</button>
          </div>
        </div>
      );
    }
    if (activeItem.result?.creativeResult) {
      return <CreativeResults creativeResult={activeItem.result.creativeResult} sources={activeItem.result.sources} onNewAnalysis={handleReset} onDeepAnalyze={handleDeepAnalyze} />;
    }
    if (activeItem.result) {
      return (
        <ResultsDisplay
          key={activeItem.id}
          result={activeItem.result}
          onNewAnalysis={handleReset}
          onSuggestionSaved={handleSuggestionSaved}
          analysisLabel={activeOption?.label}
          analysisType={activeOption?.key}
        />
      );
    }
    return null;
  }

  return (
    <AuthGate>
    {/* ── Coquille ─────────────────────────────────────────────────────
        En mode pastel : fond crème pleine page, puis un cadre arrondi à
        bordure encre (`p-frame`) qui contient toute l'application — la
        fenêtre flottante de la charte cousine de Visaly. Les classes
        `p-shell` / `p-frame` / `p-sidebar-gutter` n'ont AUCUN effet dans
        les thèmes clair / sombre / shader (cf. index.css, MODE PASTEL). */}
    <div className="p-shell h-screen flex overflow-hidden relative">
      {/* Shader background — only rendered in shader mode, lazy loaded */}
      {theme === 'shader' && (
        <Suspense fallback={null}>
          <ShaderBackground />
        </Suspense>
      )}

      <TermsGate />
      <ProfileGate />
      <DisclaimerModal />
      <AnalysisToast toasts={jobs.toasts} onOpen={handleOpenNotification} onDismiss={jobs.dismissToast} />
      <LegalNotice
        visible={legalVisible}
        onClose={() => setLegalVisible(false)}
        onNavigate={(target) => {
          setLegalVisible(false);
          setAccountInitialTab(target);
          setIsAccountOpen(true);
        }}
        onOpenDoc={(doc) => {
          if (doc === 'privacy') setPrivacyVisible(true);
          else if (doc === 'terms') setTermsVisible(true);
          else if (doc === 'subprocessors') setSubprocessorsVisible(true);
        }}
      />
      <PrivacyPolicy
        visible={privacyVisible}
        onClose={() => setPrivacyVisible(false)}
        onOpenSubprocessors={() => setSubprocessorsVisible(true)}
      />
      <TermsOfService
        visible={termsVisible}
        onClose={() => setTermsVisible(false)}
      />
      <Subprocessors
        visible={subprocessorsVisible}
        onClose={() => setSubprocessorsVisible(false)}
      />

      {/* Sidebar + popover compte — masqués pendant l'onboarding pour focaliser
          le prospect sur l'action centrale (abonnement / bundle / RDV). */}
      <div
        className={`p-frame relative flex h-full w-full overflow-hidden ${inAnalysis ? 'p-frame-tinted' : ''}`}
        style={tintVars(tintedType)}
      >
      {!isOnboarding && (
        <>
          <div className="p-sidebar-gutter relative h-full flex-shrink-0 z-40">
            <Sidebar
              history={jobs.items}
              onViewHistory={handleViewHistory}
              onRenameHistory={jobs.renameItem}
              onDeleteHistory={handleDeleteHistory}
              activeItemId={jobs.activeId}
              isMobileOpen={isMobileMenuOpen}
              setIsMobileOpen={setIsMobileMenuOpen}
              isCollapsed={isSidebarCollapsed}
              setIsCollapsed={setIsSidebarCollapsed}
              onAccountClick={() => setIsAccountOpen(prev => !prev)}
              onHomeClick={handleReset}
            />
          </div>

          {/* Account popover — rendered at root level to avoid stacking context issues */}
          <AccountPopover
            isOpen={isAccountOpen}
            onClose={() => { setIsAccountOpen(false); setAccountInitialTab('menu'); }}
            isSidebarCollapsed={isSidebarCollapsed}
            onLegalClick={() => setLegalVisible(true)}
            onSettingsClick={() => setStep('account')}
            onUpgradeClick={() => setStep('pricing')}
            onHistoriqueClick={() => setStep('historique')}
            onAdminClick={() => setStep('admin')}
            initialTab={accountInitialTab}
          />
        </>
      )}

      {/* Right column: header + content + footer */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 z-10">
        <Header
          theme={theme}
          onToggleTheme={toggleTheme}
          onMenuToggle={() => setIsMobileMenuOpen(prev => !prev)}
          runningCount={jobs.runningCount}
          queuedCount={jobs.queuedCount}
          onResumeLoader={handleResumeLoader}
          notifications={jobs.notifications}
          unreadCount={jobs.unreadCount}
          onOpenNotification={handleOpenNotification}
          onMarkAllNotificationsRead={jobs.markAllRead}
          onDismissNotification={jobs.dismiss}
          onAccountClick={() => setStep('account')}
        />

        <main className={`flex-1 overflow-y-auto scrollbar-evin ${inAnalysis ? 'tinted-fields' : ''}`}>
          <div className="max-w-5xl mx-auto w-full px-4 py-8">
            {purchase.notice && (
              <PromoNoticeBanner notice={purchase.notice} onDismiss={purchase.dismiss} />
            )}

            {step === 'select' && promo.notice && (
              <PromoNoticeBanner notice={promo.notice} onDismiss={promo.dismiss} />
            )}

            {/* Un échange de code en vol laisse l'org en `free` une fraction de
                seconde : sans cette garde, le visiteur du salon verrait passer
                l'écran d'abonnement juste avant ses analyses offertes. */}
            {step === 'select' && promo.redeeming && (
              <p className="text-center py-12 text-sm text-slate-500 dark:text-slate-400 shader:text-white/70">
                Activation de vos analyses offertes…
              </p>
            )}

            {step === 'select' && isOnboarding && !promo.redeeming && (
              <OnboardingScreen
                onViewFullPricing={() => { setPricingScrollTo('bundles'); setStep('pricing'); }}
              />
            )}

            {step === 'select' && !isOnboarding && !promo.redeeming && (
              <AnalysisTypeSelector
                options={ANALYSIS_OPTIONS}
                onSelect={handleSelectType}
                onUpgrade={() => { setPricingScrollTo(undefined); setStep('pricing'); }}
                onUnitPurchase={() => { setPricingScrollTo('bundles'); setStep('pricing'); }}
              />
            )}

            {step === 'upload' && selectedOption && selectedOption.key === 'social_post' && (
              <SocialPostHub
                error={error}
                onSubmit={handleSubmit}
                onBack={handleBack}
                creditsAvailable={features.unlimited && features.analysesSocial ? null : (organization?.creditsPerType?.social_post ?? 0)}
              />
            )}

            {step === 'upload' && selectedOption && selectedOption.key === 'creative_brief' && (
              <CreativeBriefForm
                error={error}
                onSubmit={handleSubmit}
                onBack={handleBack}
              />
            )}

            {step === 'upload' && selectedOption && selectedOption.key !== 'social_post' && selectedOption.key !== 'creative_brief' && (
              <ContentUploader
                option={selectedOption}
                error={error}
                onSubmit={handleSubmit}
                onBack={handleBack}
              />
            )}

            {(step === 'loading' || step === 'result') && renderAnalysisView()}

            {step === 'pricing' && (
              <PricingPage onClose={handleReset} scrollTo={pricingScrollTo} />
            )}

            {step === 'historique' && (
              <HistoriquePage
                onClose={handleReset}
                onViewAnalysis={(result, analysisType) => {
                  const option = ANALYSIS_OPTIONS.find((o) => o.key === (analysisType as AnalysisType)) ?? null;
                  jobs.setActiveId(null);
                  setExternalResult({ result, option });
                  setStep('result');
                }}
              />
            )}

            {step === 'account' && (
              <AccountSettingsPage
                onClose={handleReset}
                onUpgrade={() => setStep('pricing')}
              />
            )}

            {step === 'admin' && (
              <Suspense fallback={<div className="text-center py-12 text-slate-500">Chargement...</div>}>
                <AdminDashboard onClose={handleReset} />
              </Suspense>
            )}
          </div>
        </main>

        <Footer
          onLegalClick={() => setLegalVisible(true)}
          onPrivacyClick={() => setPrivacyVisible(true)}
          onTermsClick={() => setTermsVisible(true)}
          onSubprocessorsClick={() => setSubprocessorsVisible(true)}
        />
      </div>
      </div>
    </div>
    </AuthGate>
  );
}
